// src/components/Tenants/MessageTenantDialog.js
// @flow

import React, { useState, useEffect, useCallback } from 'react';
import { log } from '../../utils/jsUtils';
import { useSelector, useDispatch } from 'react-redux';
import { useI18n } from '../../contexts/I18nContext';

import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Tooltip from '@mui/material/Tooltip';
import Fab from '@mui/material/Fab';
import Typography from '@mui/material/Typography';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Popover from '@mui/material/Popover';

import LiquidMessageEditor from './LiquidMessageEditor';
import RemovableModalImage from '../SharedComponents/RemovableModalImage';
import ImageFileDrop from '../SharedComponents/ImageFileDrop';
import ForwardRef from '../SharedComponents/ForwardRef';
import SPBackend from '../../services/SPBackend';
import { calculateCreditsForMsg, submitSendMessage } from '../../models/Message';

import messageTenantDialogStyles from './Styles/MessageTenantDialog.Style';
import sharedDialogStyles from '../Styles/SharedDialogStyles';
import TagRenderer from '../SharedComponents/TagRenderer';
import { useTheme } from '@mui/material/styles';
import { createClasses } from '../../utils/stylesUtils';

import type { Tenant } from '../../services/SPBackend';
import { selectUser, selectAccountUsage } from '../../reduxStore/modules/user/selectors';
import { selectAllTags } from '../../reduxStore/modules/tags/selectors';
import { selectBillingState } from '../../reduxStore/modules/billing/selectors';
import { setSnackMessage } from '../../reduxStore/modules/common';
import { setRechargePopup, setUpgradePopup } from '../../reduxStore/modules/drawer/actions';

// Constant for error message initial state - defined outside component to prevent recreation
const ERROR_MSG_INIT_STATE = { function: 'upgrade', msg: '' };

type Props = {
  showToFilters: boolean, // if false => direct to single tenant
  tenant: ?Tenant,
  setOpen: (val: boolean) => void,
  open: boolean,
  triggerAction: Function
};

const MessageTenantDialog = (props: Props) => {
  const {
    showToFilters,
    tenant,
    setOpen,
    open,
    triggerAction
  } = props;

  const dispatch = useDispatch();

  // Get data from Redux store
  const i18n = useI18n();
  const user = useSelector(selectUser);
  const billing = useSelector(selectBillingState);
  const accountUsage = useSelector(selectAccountUsage);
  const allTags = useSelector(selectAllTags);

  const theme = useTheme();
  // Merge shared dialog styles with component-specific styles
  const mergedStyles = { ...sharedDialogStyles, ...messageTenantDialogStyles };
  const classes = createClasses(mergedStyles, theme);

  // Local state
  const [message, setMessage] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState([]);
  const [toTenants, setToTenants] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState('');
  const [attachment, setAttachment] = useState(null);
  const [creditsUsed, setCreditsUsed] = useState(0);

  // For "upgrade" or "recharge" errors
  const [errorMsg, setErrorMsg] = useState(ERROR_MSG_INIT_STATE);

  // For tag picker popover
  const [addTagAnchorEl, setAddTagAnchorEl] = useState(null);

  // For send confirmation dialog
  const [showConfirmSend, setShowConfirmSend] = useState(false);

  // "initialAssignedStaff" logic from your old code is replaced by "tenant" or broadcast approach
  // or keep it if you had it…

  const emptyForm = () => {
    setMessage('');
    setSelectedTagIds([]);
    setToTenants([]);
    setAttachment(null);
  };

  const emptyErrorMsg = useCallback(() => {
    setErrorMsg(ERROR_MSG_INIT_STATE);
    setError('');
    // Don't reset isSending - it's managed by the send flow
  }, []); // No dependencies - ERROR_MSG_INIT_STATE is a stable constant

  const refreshRecipientsCount = (tagIds) => {
    SPBackend.findTenantsIds(
      tagIds || [],
      user
    )
      .then((response) => {
        const { tenants } = response.data;
        setToTenants(tenants);
      })
      .catch((e) => {
        log('[refreshRecipientsCount] error: ', e);
      });
  };

  // Handler for tag addition (from Autocomplete selection)
  const handleTagAdded = (event, newValue) => {
    if (newValue) {
      const newIds = [...selectedTagIds, newValue._id];
      setSelectedTagIds(newIds);
      refreshRecipientsCount(newIds);
      emptyErrorMsg();
    }
  };

  // Handler for tag removal (from TagRenderer's "x" button)
  const handleTagRemoved = (tag) => {
    const newIds = selectedTagIds.filter(id => id !== tag._id);
    setSelectedTagIds(newIds);
    refreshRecipientsCount(newIds);
  };

  // Handlers for tag picker popover
  const handleAddTagClick = (event) => {
    event.stopPropagation();
    setAddTagAnchorEl(event.currentTarget);
  };

  const handleAddTagClose = () => {
    setAddTagAnchorEl(null);
  };

  // Handler for tag selection from popover
  const handleTagSelected = (event, newValue) => {
    if (newValue) {
      const newIds = [...selectedTagIds, newValue._id];
      setSelectedTagIds(newIds);
      refreshRecipientsCount(newIds);
      emptyErrorMsg();
      handleAddTagClose();
    }
  };

  const removeAttachment = (att) => (e) => {
    e.stopPropagation();
    setAttachment(null);
  };

  const handleSetAttachment = useCallback((attachmentObj) => {
    log('[handleSetAttachment] attachmentObj: ', attachmentObj);
    setAttachment(attachmentObj);
  }, []);

  const resetForm = () => {
    setOpen(false);
    setSelectedTagIds([]);
    setToTenants([]);
    setErrorMsg(ERROR_MSG_INIT_STATE);
    setCreditsUsed(0);
  };

  const openRechargeAccount = () => {
    log('[openRechargeAccount]...');
    setIsSending(false);
    dispatch(setRechargePopup(true));
  };

  const openUpgradePlan = () => {
    log('[openUpgradePlan]...');
    setIsSending(false);
    dispatch(setUpgradePopup(true));
  };

  useEffect(() => {
    if (open && showToFilters) {
      refreshRecipientsCount([]);
    }
  }, [open, showToFilters]);

  useEffect(() => {
    // Recalculate credits for the message (not multiplied by recipients)
    const creditsPerMessage = calculateCreditsForMsg({ message: message, attachment: attachment });
    setCreditsUsed(creditsPerMessage);
  }, [message, attachment]);

  useEffect(() => {
    // Clear errors when user starts typing (only if errors exist)
    if (message && (error || errorMsg.msg)) {
      emptyErrorMsg();
    }
  }, [message, error, errorMsg.msg, emptyErrorMsg]);

  useEffect(() => {
    if (accountUsage?.TotalCreditsBalance > 0 && billing.subscriptionStatus !== 'canceled') {
      emptyErrorMsg();
    }
  }, [accountUsage?.TotalCreditsBalance, billing.subscriptionStatus, emptyErrorMsg]);

  const handleCloseDialog = () => {
    resetForm();
  };

  const broadcastFormSubmit = (e) => {
    e.preventDefault();
    const _props = {
      message,
      attachments: [attachment],
      setErrorMsg,
      setIsSending,
      toTenantsIds: toTenants.map((t) => t._id),
      setOpenDialog: setOpen,
      triggerAction,
      emptyForm,
      billing,
      accountUsage,
      dispatch
    };
    log('[broadcastFormSubmit] _props: ', _props);
    submitSendMessage(_props);
  };

  const singleTenantFormSubmit = (e) => {
    e.preventDefault();
    const _props = {
      message,
      attachments: [attachment],
      setErrorMsg,
      setIsSending,
      toTenantsIds: [tenant?._id],
      setOpenDialog: setOpen,
      triggerAction,
      emptyForm,
      billing,
      accountUsage,
      dispatch
    };
    log('[singleTenantFormSubmit] _props: ', _props);
    submitSendMessage(_props);
  };

  const handleSend = () => {
    log('[handleSend]...');

    // Basic validation
    if (message === '') {
      setError(i18n.t('errors.empty_message'));
      return;
    }

    if ((showToFilters && !toTenants.length) || (!showToFilters && !tenant)) {
      setError(i18n.t('errors.no_recipient'));
      return;
    }

    // Show confirmation dialog instead of sending directly
    setShowConfirmSend(true);
  };

  const handleConfirmSend = () => {
    log('[handleConfirmSend]...');
    setShowConfirmSend(false);

    // Delegate to appropriate submit function based on mode
    if (showToFilters) {
      // Broadcast mode
      const _props = {
        message,
        attachments: [attachment],
        setErrorMsg,
        setIsSending,
        toTenantsIds: toTenants.map((t) => t._id),
        setOpenDialog: setOpen,
        triggerAction,
        emptyForm,
        billing,
        accountUsage,
        dispatch
      };
      log('[handleConfirmSend] Broadcasting to', toTenants.length, 'recipients');
      submitSendMessage(_props);
    } else {
      // Direct message mode
      const _props = {
        message,
        attachments: [attachment],
        setErrorMsg,
        setIsSending,
        toTenantsIds: [tenant?._id],
        setOpenDialog: setOpen,
        triggerAction,
        emptyForm,
        billing,
        accountUsage,
        dispatch
      };
      log('[handleConfirmSend] Sending direct message to tenant:', tenant?._id);
      submitSendMessage(_props);
    }
  };

  useEffect(() => {
    // If a single tenant was provided, reset the tenant list
    if (!showToFilters && tenant) {
      setToTenants([tenant]);
    }
  }, [tenant, showToFilters]);
  
  useEffect(() => {
    // Reset states when dialog is opened
    if (open) {
      emptyErrorMsg();
    }
  }, [open, emptyErrorMsg]);

  // Close handler
  const handleClose = () => {
    emptyForm();
    resetForm();
  };

  // Label for send button
  const getSendBtnLabel = () => {
    if (tenant) {
      return i18n.t('buttons.send_direct_message');
    }
    return i18n.t('buttons.send_broadcast_message');
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      scroll="paper"
    >
      {/* DialogTitle for Broadcast Mode - Single row layout with Autocomplete */}
      {showToFilters && (
        <DialogTitle
          component="div"
          sx={{
            padding: '16px 24px 8px 24px',
            '& *': { fontSize: 'inherit', fontWeight: 'inherit' }
          }}
          disableTypography
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Typography component="span" sx={{ fontSize: theme.typography?.content?.fontSize || '0.875rem', fontWeight: 700, flexShrink: 0 }}>
              To:
            </Typography>

            {/* Display selected tags using TagRenderer with removal support */}
            {selectedTagIds.length > 0 && (
              <TagRenderer
                tagsIds={selectedTagIds}
                onTagRemoved={handleTagRemoved}
                containerStyle={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}
              />
            )}

            {/* Show "All customers" with caret when no tags, or + button when tags exist */}
            {selectedTagIds.length === 0 ? (
              <Box
                onClick={handleAddTagClick}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  color: 'text.secondary',
                  '&:hover': { color: 'text.primary' }
                }}
              >
                <Typography sx={{ fontSize: theme.typography?.content?.fontSize || '0.875rem' }}>
                  All customers
                </Typography>
                <ArrowDropDownIcon sx={{ fontSize: 20 }} />
              </Box>
            ) : (
              <Box
                onClick={handleAddTagClick}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'white',
                  color: '#666',
                  height: '24px',
                  width: '24px',
                  padding: 0,
                  borderRadius: '24px',
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  border: '1px solid #ddd',
                  opacity: 0.6,
                  transition: 'opacity 0.2s ease',
                  '&:hover': { opacity: 1 }
                }}
              >
                <AddIcon fontSize="small" />
              </Box>
            )}

            {/* Dropdown popover for tag selection */}
            <Popover
              open={Boolean(addTagAnchorEl)}
              anchorEl={addTagAnchorEl}
              onClose={handleAddTagClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              slotProps={{
                paper: {
                  sx: { p: 1, minWidth: 200, overflow: 'visible' }
                }
              }}
            >
              <Autocomplete
                options={allTags.filter(tag => !selectedTagIds.includes(tag._id))}
                value={null}
                onChange={handleTagSelected}
                getOptionLabel={(option) => option.Name || option.name || ''}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Search tags..."
                    size="small"
                    autoFocus
                  />
                )}
                renderOption={(props, tag) => (
                  <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: '50%',
                        backgroundColor: tag.Color || tag.color || '#ccc',
                        flexShrink: 0
                      }}
                    />
                    {tag.Name || tag.name}
                  </Box>
                )}
                sx={{ width: '100%' }}
                size="small"
                openOnFocus
                disableClearable
                disablePortal
              />
            </Popover>

            <Tooltip title={`${toTenants.length} recipient${toTenants.length !== 1 ? 's' : ''}`}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 'auto', color: 'text.secondary' }}>
                <PersonOutlineIcon sx={{ fontSize: 18 }} />
                <Typography sx={{ fontSize: theme.typography?.content?.fontSize || '0.875rem' }}>{toTenants.length}</Typography>
              </Box>
            </Tooltip>
          </Box>
        </DialogTitle>
      )}

      {/* DialogTitle for Direct Message Mode */}
      {!showToFilters && (
        <DialogTitle sx={classes.messageTenantDialogTitle} disableTypography>
          {'To: '}
          <Box component="span" sx={{ fontWeight: 'bold' }}>
            {tenant ? `${tenant.FirstName} ${tenant.LastName}` : ''}
          </Box>
        </DialogTitle>
      )}

      <DialogContent
        sx={{
          // Mobile-friendly scroll behavior for keyboard
          overflowY: 'auto',
          maxHeight: { xs: '50vh', sm: '70vh' },
          WebkitOverflowScrolling: 'touch',
          paddingTop: 0,
          paddingBottom: 0
        }}
      >

        <Grid container spacing={2} sx={classes.secondInput}>
          <Grid item xs={12} sx={{ pt: '0 !important' }}>
            <LiquidMessageEditor
              message={message}
              setMessage={setMessage}
              emptyErrorMsg={emptyErrorMsg}
              recipients={toTenants}
              isSending={isSending}
              placeholder={i18n.t('placeholders.compose_message')}
              creditsUsed={creditsUsed}
              isBroadcast={!!showToFilters}
            />
          </Grid>

          {attachment && (
            <Grid item xs={12}>
              <RemovableModalImage
                src={attachment?.Url}
                alt="Message attachment"
                onRemove={removeAttachment(attachment)}
              />
            </Grid>
          )}
        </Grid>

        {error && (
          <Grid container spacing={2} sx={classes.errorMessagesCtr}>
            <Grid item xs={12}>
              <DialogContentText
                sx={classes.error}
                color="error"
              >
                {error}
              </DialogContentText>
            </Grid>

            {errorMsg.msg && (
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={errorMsg.function === 'upgrade' ? openUpgradePlan : openRechargeAccount}
                >
                  {errorMsg.function === 'upgrade'
                    ? i18n.t('buttons.upgrade_now')
                    : i18n.t('buttons.recharge_now')}
                </Button>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      <DialogActions sx={classes.actionsCtr}>
        <Box>
          <ImageFileDrop onImageReceived={handleSetAttachment}>
            <Tooltip title="Attach an image" placement="top">
              <Fab sx={classes.actionAttachImgBtn}>
                <ImageOutlinedIcon />
              </Fab>
            </Tooltip>
          </ImageFileDrop>
        </Box>
        <Box>
          <Button onClick={handleClose} color="primary">
            {i18n.t('buttons.cancel')}
          </Button>
          <Button
            onClick={handleSend}
            color="primary"
            variant="contained"
            disabled={isSending}
          >
            {isSending ? i18n.t('buttons.sending') : getSendBtnLabel()}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>

    {/* Send Confirmation Dialog */}
    <Dialog
      open={showConfirmSend}
      onClose={() => setShowConfirmSend(false)}
    >
      <DialogTitle>Confirm Send</DialogTitle>
      <DialogContent>
        <DialogContentText>
          You are about to send this message to <strong>{toTenants.length}</strong> recipient{toTenants.length !== 1 ? 's' : ''}.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button color="primary" onClick={() => setShowConfirmSend(false)}>
          Cancel
        </Button>
        <Button color="primary" variant="contained" onClick={handleConfirmSend}>
          Send
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default MessageTenantDialog;